/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import ExcelJS from 'exceljs';
import { SafetyActivity, CollaboratorEvaluation, ILULevel } from '../types.ts';
import { ILU_LEVEL_DEFINITIONS } from '../data/competenciesInitialData.ts';
import { CORPORATE_LOGO_BASE64 } from './corporateLogoBase64.ts';

export interface CompetenciesExcelExportOptions {
  fileName?: string;
  reportTitle?: string;
  costCenterName?: string;
  generatedBy?: string;
  includeExecutiveSummary?: boolean;
  includeLevelSheets?: boolean;
  includeActivitiesCatalog?: boolean;
}

/**
 * Inserts the corporate header banner and logo into an Excel worksheet.
 */
function applyCorporateHeader(
  sheet: ExcelJS.Worksheet,
  workbook: ExcelJS.Workbook,
  title: string,
  subtitle: string,
  costCenterName: string,
  generatedBy: string,
  endColLetter: string = 'K'
) {
  // Logo
  try {
    if (CORPORATE_LOGO_BASE64 && CORPORATE_LOGO_BASE64.startsWith('data:image')) {
      const logoId = workbook.addImage({
        base64: CORPORATE_LOGO_BASE64,
        extension: 'jpeg'
      });
      sheet.addImage(logoId, {
        tl: { col: 0.2, row: 0.3 },
        ext: { width: 140, height: 46 },
        editAs: 'oneCell'
      });
    }
  } catch (e) {
    console.warn('Could not insert logo in Excel sheet:', e);
  }

  // Row 1: Spacing
  sheet.getRow(1).height = 12;

  // Row 2: Title
  sheet.mergeCells(`C2:${endColLetter}2`);
  const titleCell = sheet.getCell('C2');
  titleCell.value = title;
  titleCell.font = { name: 'Arial', size: 13, bold: true, color: { argb: 'FFFFFF' } };
  titleCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'BC1100' } // Corporate Red
  };
  titleCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  sheet.getRow(2).height = 30;

  // Row 3: Subtitle
  sheet.mergeCells(`C3:${endColLetter}3`);
  const subCell = sheet.getCell('C3');
  subCell.value = subtitle;
  subCell.font = { name: 'Arial', size: 9.5, italic: true, color: { argb: 'FFFFFF' } };
  subCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: '1E293B' } // Slate-800
  };
  subCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  sheet.getRow(3).height = 20;

  // Row 4: Metadata Bar
  sheet.mergeCells(`A4:${endColLetter}4`);
  const metaCell = sheet.getCell('A4');
  const now = new Date();
  const dateStr = now.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
  const timeStr = now.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  metaCell.value = `📍 Sede / Planta: ${costCenterName}   |   📅 Fecha de Auditoría: ${dateStr} ${timeStr}   |   👤 Auditor HSEQ: ${generatedBy}`;
  metaCell.font = { name: 'Arial', size: 8.5, bold: true, color: { argb: '334155' } };
  metaCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F1F5F9' }
  };
  metaCell.alignment = { horizontal: 'center', vertical: 'middle' };
  sheet.getRow(4).height = 22;

  sheet.getRow(5).height = 10; // Spacing
}

/**
 * Downloads a complete, professional Excel workbook containing:
 * 1. Resumen Ejecutivo y Matriz ILU Consolidada
 * 2. Personal Nivel U (Autónomos / Capacitados)
 * 3. Personal Nivel L (En Desarrollo / Supervisados)
 * 4. Personal Nivel I (Inducción / En Formación)
 * 5. Catálogo de Actividades y Competencias de Seguridad
 */
export async function exportCompetenciesMatrixToExcel(
  activities: SafetyActivity[],
  evaluations: CollaboratorEvaluation[],
  options: CompetenciesExcelExportOptions = {}
): Promise<void> {
  const {
    fileName = `Matriz_Competencias_ILU_Seguridad_${new Date().toISOString().split('T')[0]}.xlsx`,
    reportTitle = 'SISTEMA DE GESTIÓN HSEQ - MATRIZ DE COMPETENCIAS OPERATIVAS Y DE SEGURIDAD (MÉTODO I.L.U.)',
    costCenterName = 'Sede Operativa Bajío - Planta Industrial',
    generatedBy = 'Coordinación de Seguridad y Salud en el Trabajo',
    includeExecutiveSummary = true,
    includeLevelSheets = true,
    includeActivitiesCatalog = true
  } = options;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'SNF México - HSEQ Security & Competency Management';
  workbook.lastModifiedBy = generatedBy;
  workbook.created = new Date();
  workbook.modified = new Date();

  // Aggregate statistics
  const totalActivities = activities.length;
  const totalEvaluations = evaluations.length;
  
  // Unique collaborators
  const uniqueEmployees = new Map<string, { id: string; name: string; position: string; area: string; shift: string }>();
  evaluations.forEach(ev => {
    if (ev.employeeId && !uniqueEmployees.has(ev.employeeId)) {
      uniqueEmployees.set(ev.employeeId, {
        id: ev.employeeId,
        name: ev.employeeName,
        position: ev.position,
        area: ev.area,
        shift: ev.shift
      });
    }
  });
  const totalEmployees = uniqueEmployees.size;

  const countU = evaluations.filter(e => e.level === 'U').length;
  const countL = evaluations.filter(e => e.level === 'L').length;
  const countI = evaluations.filter(e => e.level === 'I').length;
  const countO = evaluations.filter(e => e.level === 'O').length;

  const pctU = totalEvaluations > 0 ? Math.round(((countU + countO) / totalEvaluations) * 100) : 0;
  const pctL = totalEvaluations > 0 ? Math.round((countL / totalEvaluations) * 100) : 0;
  const pctI = totalEvaluations > 0 ? Math.round((countI / totalEvaluations) * 100) : 0;

  // =========================================================================
  // HOJA 1: RESUMEN EJECUTIVO Y MATRIZ CONSOLIDADA
  // =========================================================================
  if (includeExecutiveSummary) {
    const ws1 = workbook.addWorksheet('Resumen y Matriz ILU', {
      views: [{ showGridLines: true }]
    });

    applyCorporateHeader(
      ws1,
      workbook,
      reportTitle,
      'Evaluación del Nivel de Dominio, Autonomía y Capacitación en Actividades Críticas de Seguridad',
      costCenterName,
      generatedBy,
      'L'
    );

    // KPI Cards Block
    let rIdx = 6;
    ws1.mergeCells(`A${rIdx}:L${rIdx}`);
    const kpiTitle = ws1.getCell(`A${rIdx}`);
    kpiTitle.value = 'INDICADORES CLAVE DE COBERTURA Y APTITUD OPERATIVA (I.L.U.)';
    kpiTitle.font = { name: 'Arial', size: 10, bold: true, color: { argb: '0F172A' } };
    kpiTitle.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E2E8F0' } };
    kpiTitle.alignment = { horizontal: 'center', vertical: 'middle' };
    ws1.getRow(rIdx).height = 24;

    rIdx++;
    // KPI headers
    const kpiHeaders = [
      { col1: 'A', col2: 'B', val: 'ACTIVIDADES DE SEGURIDAD', color: '1E293B' },
      { col1: 'C', col2: 'D', val: 'COLABORADORES EVALUADOS', color: '1E293B' },
      { col1: 'E', col2: 'F', val: 'TOTAL EVALUACIONES', color: '1E293B' },
      { col1: 'G', col2: 'H', val: 'NIVEL U (AUTÓNOMOS)', color: '065F46' }, // Dark green
      { col1: 'I', col2: 'J', val: 'NIVEL L (SUPERVISADOS)', color: '0369A1' }, // Dark sky
      { col1: 'K', col2: 'L', val: 'NIVEL I (INDUCCIÓN)', color: '9A3412' }  // Dark amber
    ];

    kpiHeaders.forEach(k => {
      ws1.mergeCells(`${k.col1}${rIdx}:${k.col2}${rIdx}`);
      const c = ws1.getCell(`${k.col1}${rIdx}`);
      c.value = k.val;
      c.font = { name: 'Arial', size: 8.5, bold: true, color: { argb: 'FFFFFF' } };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: k.color } };
      c.alignment = { horizontal: 'center', vertical: 'middle' };
    });
    ws1.getRow(rIdx).height = 20;

    rIdx++;
    // KPI values
    const kpiValues = [
      { col1: 'A', col2: 'B', val: `${totalActivities} Tareas`, sub: 'Catálogo Vigente', color: 'F8FAFC' },
      { col1: 'C', col2: 'D', val: `${totalEmployees} Personas`, sub: 'Personal Activo', color: 'F8FAFC' },
      { col1: 'E', col2: 'F', val: `${totalEvaluations} Registros`, sub: 'Historial', color: 'F8FAFC' },
      { col1: 'G', col2: 'H', val: `${countU + countO} (${pctU}%)`, sub: '100% Aptos e Independientes', color: 'ECFDF5', textCol: '047857' },
      { col1: 'I', col2: 'J', val: `${countL} (${pctL}%)`, sub: 'En Práctica con Tutor', color: 'F0F9FF', textCol: '0284C7' },
      { col1: 'K', col2: 'L', val: `${countI} (${pctI}%)`, sub: 'En Inducción Teórica', color: 'FFFBEB', textCol: 'D97706' }
    ];

    kpiValues.forEach(k => {
      ws1.mergeCells(`${k.col1}${rIdx}:${k.col2}${rIdx}`);
      const c = ws1.getCell(`${k.col1}${rIdx}`);
      c.value = k.val;
      c.font = { name: 'Arial', size: 12, bold: true, color: { argb: k.textCol || '0F172A' } };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: k.color } };
      c.alignment = { horizontal: 'center', vertical: 'middle' };
      c.border = {
        top: { style: 'thin', color: { argb: 'CBD5E1' } },
        bottom: { style: 'thin', color: { argb: 'CBD5E1' } },
        left: { style: 'thin', color: { argb: 'CBD5E1' } },
        right: { style: 'thin', color: { argb: 'CBD5E1' } }
      };
    });
    ws1.getRow(rIdx).height = 30;

    // Explanatory Note on ILU
    rIdx += 2;
    ws1.mergeCells(`A${rIdx}:L${rIdx}`);
    const legendCell = ws1.getCell(`A${rIdx}`);
    legendCell.value = 'CRITERIO DE CLASIFICACIÓN: [ I ] = Inducción / Conoce la teoría (Acompañamiento 100%)  |  [ L ] = En Desarrollo / Realiza bajo supervisión  |  [ U ] = Autónomo / Capacitado (Apto 100%)  |  [ O ] = Instructor / Auditor Master';
    legendCell.font = { name: 'Arial', size: 8, bold: true, color: { argb: '475569' } };
    legendCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F8FAFC' } };
    legendCell.alignment = { horizontal: 'center', vertical: 'middle' };
    ws1.getRow(rIdx).height = 18;

    // Cross-Matrix Table: Collaborators vs Activities
    rIdx += 2;
    ws1.mergeCells(`A${rIdx}:L${rIdx}`);
    const matrixSecTitle = ws1.getCell(`A${rIdx}`);
    matrixSecTitle.value = 'MATRIZ CRUZADA DE COBERTURA: COLABORADORES VS ACTIVIDADES DE SEGURIDAD';
    matrixSecTitle.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFF' } };
    matrixSecTitle.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1E293B' } };
    matrixSecTitle.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    ws1.getRow(rIdx).height = 24;

    rIdx++;
    // Headers for table
    const tableHeaders = [
      'No.',
      'ID / Nómina',
      'Nombre del Colaborador',
      'Puesto / Rol',
      'Área',
      'Turno',
      'Código Actividad',
      'Actividad Evaluada',
      'Nivel I.L.U.',
      'Calificación',
      'Fecha Eval.',
      'Auditor / Evaluador'
    ];

    const thRow = ws1.getRow(rIdx);
    tableHeaders.forEach((th, idx) => {
      const cell = thRow.getCell(idx + 1);
      cell.value = th;
      cell.font = { name: 'Arial', size: 9, bold: true, color: { argb: 'FFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '334155' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin', color: { argb: '1E293B' } },
        bottom: { style: 'thin', color: { argb: '1E293B' } },
        left: { style: 'thin', color: { argb: '1E293B' } },
        right: { style: 'thin', color: { argb: '1E293B' } }
      };
    });
    thRow.height = 24;

    // Data rows
    evaluations.forEach((ev, idx) => {
      rIdx++;
      const row = ws1.getRow(rIdx);
      const isEven = idx % 2 === 0;
      const bg = isEven ? 'FFFFFF' : 'F8FAFC';

      row.getCell(1).value = idx + 1;
      row.getCell(2).value = ev.employeeId || 'S/N';
      row.getCell(3).value = ev.employeeName;
      row.getCell(4).value = ev.position || 'Operador';
      row.getCell(5).value = ev.area || 'General';
      row.getCell(6).value = ev.shift || 'Turno 1';
      row.getCell(7).value = ev.activityCode;
      row.getCell(8).value = ev.activityName;
      
      // Level cell styling
      const lvlCell = row.getCell(9);
      lvlCell.value = ev.level;
      if (ev.level === 'U' || ev.level === 'O') {
        lvlCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DCFCE7' } }; // Light green
        lvlCell.font = { name: 'Arial', size: 10, bold: true, color: { argb: '166534' } }; // Dark green
      } else if (ev.level === 'L') {
        lvlCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E0F2FE' } }; // Light sky
        lvlCell.font = { name: 'Arial', size: 10, bold: true, color: { argb: '0369A1' } }; // Dark sky
      } else {
        lvlCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEF3C7' } }; // Light amber
        lvlCell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'B45309' } }; // Dark amber
      }

      row.getCell(10).value = ev.score ? `${ev.score}%` : '100%';
      row.getCell(11).value = ev.evaluationDate || '-';
      row.getCell(12).value = ev.evaluator || 'Líder HSEQ';

      // Formatting
      for (let c = 1; c <= 12; c++) {
        const cell = row.getCell(c);
        if (c !== 9) {
          cell.font = { name: 'Arial', size: 8.5, color: { argb: '1E293B' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
        }
        cell.alignment = {
          vertical: 'middle',
          horizontal: [1, 2, 6, 7, 9, 10, 11].includes(c) ? 'center' : 'left'
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'E2E8F0' } },
          bottom: { style: 'thin', color: { argb: 'E2E8F0' } },
          left: { style: 'thin', color: { argb: 'E2E8F0' } },
          right: { style: 'thin', color: { argb: 'E2E8F0' } }
        };
      }
      row.height = 20;
    });

    // Column widths
    ws1.getColumn(1).width = 6;
    ws1.getColumn(2).width = 14;
    ws1.getColumn(3).width = 28;
    ws1.getColumn(4).width = 24;
    ws1.getColumn(5).width = 18;
    ws1.getColumn(6).width = 16;
    ws1.getColumn(7).width = 14;
    ws1.getColumn(8).width = 34;
    ws1.getColumn(9).width = 12;
    ws1.getColumn(10).width = 14;
    ws1.getColumn(11).width = 14;
    ws1.getColumn(12).width = 24;
  }

  // =========================================================================
  // HOJAS INDIVIDUALES POR NIVEL DE EVALUACIÓN (U, L, I)
  // =========================================================================
  if (includeLevelSheets) {
    const levelsToExport: { level: ILULevel; sheetName: string; title: string; colorHeader: string; colorRow: string; textCol: string }[] = [
      {
        level: 'U',
        sheetName: 'Personal Nivel U - Autónomos',
        title: 'PERSONAL CERTIFICADO NIVEL U - AUTÓNOMOS (100% CAPACITADOS PARA OPERAR)',
        colorHeader: '047857', // Emerald
        colorRow: 'F0FDF4',
        textCol: '166534'
      },
      {
        level: 'L',
        sheetName: 'Personal Nivel L - Supervisados',
        title: 'PERSONAL EN DESARROLLO NIVEL L - EJECUCIÓN CON SUPERVISIÓN PERIÓDICA',
        colorHeader: '0284C7', // Sky Blue
        colorRow: 'F0F9FF',
        textCol: '0369A1'
      },
      {
        level: 'I',
        sheetName: 'Personal Nivel I - Inducción',
        title: 'PERSONAL EN INDUCCIÓN NIVEL I - NO AUTORIZADO PARA OPERAR EN SOLITARIO',
        colorHeader: 'D97706', // Amber
        colorRow: 'FFFBEB',
        textCol: 'B45309'
      }
    ];

    levelsToExport.forEach(lvlConfig => {
      const wsLvl = workbook.addWorksheet(lvlConfig.sheetName, {
        views: [{ showGridLines: true }]
      });

      applyCorporateHeader(
        wsLvl,
        workbook,
        lvlConfig.title,
        `Filtrado exclusivo de colaboradores clasificados en ${ILU_LEVEL_DEFINITIONS[lvlConfig.level].label}`,
        costCenterName,
        generatedBy,
        'L'
      );

      const filtered = evaluations.filter(e => e.level === lvlConfig.level || (lvlConfig.level === 'U' && e.level === 'O'));

      // Section Banner
      let rIdx = 6;
      wsLvl.mergeCells(`A${rIdx}:L${rIdx}`);
      const banner = wsLvl.getCell(`A${rIdx}`);
      banner.value = `TOTAL DE REGISTROS EN ESTE NIVEL: ${filtered.length} COLABORADORES EVALUADOS   |   ${ILU_LEVEL_DEFINITIONS[lvlConfig.level].shortDesc}`;
      banner.font = { name: 'Arial', size: 9.5, bold: true, color: { argb: 'FFFFFF' } };
      banner.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: lvlConfig.colorHeader } };
      banner.alignment = { horizontal: 'center', vertical: 'middle' };
      wsLvl.getRow(rIdx).height = 26;

      rIdx += 2;
      const headers = [
        'No.',
        'Código Tarea',
        'Actividad de Seguridad',
        'ID / Nómina',
        'Nombre del Colaborador',
        'Puesto / Cargo',
        'Área',
        'Turno',
        'Nivel',
        'Calificación',
        'Fecha Evaluación',
        'Auditor / Evaluador'
      ];

      const thRow = wsLvl.getRow(rIdx);
      headers.forEach((h, idx) => {
        const c = thRow.getCell(idx + 1);
        c.value = h;
        c.font = { name: 'Arial', size: 9, bold: true, color: { argb: 'FFFFFF' } };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1E293B' } };
        c.alignment = { horizontal: 'center', vertical: 'middle' };
        c.border = {
          top: { style: 'thin', color: { argb: '0F172A' } },
          bottom: { style: 'thin', color: { argb: '0F172A' } },
          left: { style: 'thin', color: { argb: '0F172A' } },
          right: { style: 'thin', color: { argb: '0F172A' } }
        };
      });
      thRow.height = 24;

      if (filtered.length === 0) {
        rIdx++;
        wsLvl.mergeCells(`A${rIdx}:L${rIdx}`);
        const emptyCell = wsLvl.getCell(`A${rIdx}`);
        emptyCell.value = `No se encontraron colaboradores registrados en ${ILU_LEVEL_DEFINITIONS[lvlConfig.level].label}.`;
        emptyCell.font = { name: 'Arial', size: 9.5, italic: true, color: { argb: '64748B' } };
        emptyCell.alignment = { horizontal: 'center', vertical: 'middle' };
        wsLvl.getRow(rIdx).height = 30;
      } else {
        filtered.forEach((ev, idx) => {
          rIdx++;
          const row = wsLvl.getRow(rIdx);
          const isEven = idx % 2 === 0;
          const bg = isEven ? 'FFFFFF' : lvlConfig.colorRow;

          row.getCell(1).value = idx + 1;
          row.getCell(2).value = ev.activityCode;
          row.getCell(3).value = ev.activityName;
          row.getCell(4).value = ev.employeeId || 'S/N';
          row.getCell(5).value = ev.employeeName;
          row.getCell(6).value = ev.position || 'Operador';
          row.getCell(7).value = ev.area || 'General';
          row.getCell(8).value = ev.shift || 'Turno 1';
          
          const lvlCell = row.getCell(9);
          lvlCell.value = ev.level;
          lvlCell.font = { name: 'Arial', size: 10, bold: true, color: { argb: lvlConfig.textCol } };
          lvlCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: lvlConfig.colorRow } };

          row.getCell(10).value = ev.score ? `${ev.score}%` : '100%';
          row.getCell(11).value = ev.evaluationDate || '-';
          row.getCell(12).value = ev.evaluator || 'Líder HSEQ';

          for (let c = 1; c <= 12; c++) {
            const cell = row.getCell(c);
            if (c !== 9) {
              cell.font = { name: 'Arial', size: 8.5, color: { argb: '1E293B' } };
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
            }
            cell.alignment = {
              vertical: 'middle',
              horizontal: [1, 2, 4, 8, 9, 10, 11].includes(c) ? 'center' : 'left'
            };
            cell.border = {
              top: { style: 'thin', color: { argb: 'CBD5E1' } },
              bottom: { style: 'thin', color: { argb: 'CBD5E1' } },
              left: { style: 'thin', color: { argb: 'CBD5E1' } },
              right: { style: 'thin', color: { argb: 'CBD5E1' } }
            };
          }
          row.height = 20;
        });
      }

      wsLvl.getColumn(1).width = 6;
      wsLvl.getColumn(2).width = 14;
      wsLvl.getColumn(3).width = 34;
      wsLvl.getColumn(4).width = 14;
      wsLvl.getColumn(5).width = 28;
      wsLvl.getColumn(6).width = 24;
      wsLvl.getColumn(7).width = 18;
      wsLvl.getColumn(8).width = 16;
      wsLvl.getColumn(9).width = 10;
      wsLvl.getColumn(10).width = 14;
      wsLvl.getColumn(11).width = 14;
      wsLvl.getColumn(12).width = 24;
    });
  }

  // =========================================================================
  // HOJA: CATÁLOGO DE ACTIVIDADES DE SEGURIDAD
  // =========================================================================
  if (includeActivitiesCatalog) {
    const wsCat = workbook.addWorksheet('Catálogo Actividades', {
      views: [{ showGridLines: true }]
    });

    applyCorporateHeader(
      wsCat,
      workbook,
      'CATÁLOGO OFICIAL DE ACTIVIDADES CRÍTICAS Y ESTÁNDARES DE SEGURIDAD',
      'Registro de procedimientos, niveles de riesgo, EPP aplicable y normas de referencia STPS / ISO',
      costCenterName,
      generatedBy,
      'I'
    );

    let rIdx = 6;
    const catHeaders = [
      'No.',
      'Código',
      'Nombre de la Actividad de Seguridad',
      'Categoría / Tipo',
      'Área / Proceso',
      'Nivel de Riesgo',
      'Norma / Estándar',
      'EPP Requerido',
      'Evaluados (U / L / I)'
    ];

    const thRow = wsCat.getRow(rIdx);
    catHeaders.forEach((h, idx) => {
      const c = thRow.getCell(idx + 1);
      c.value = h;
      c.font = { name: 'Arial', size: 9, bold: true, color: { argb: 'FFFFFF' } };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1E293B' } };
      c.alignment = { horizontal: 'center', vertical: 'middle' };
      c.border = {
        top: { style: 'thin', color: { argb: '0F172A' } },
        bottom: { style: 'thin', color: { argb: '0F172A' } },
        left: { style: 'thin', color: { argb: '0F172A' } },
        right: { style: 'thin', color: { argb: '0F172A' } }
      };
    });
    thRow.height = 24;

    activities.forEach((act, idx) => {
      rIdx++;
      const row = wsCat.getRow(rIdx);
      const isEven = idx % 2 === 0;
      const bg = isEven ? 'FFFFFF' : 'F8FAFC';

      const evsForAct = evaluations.filter(e => e.activityId === act.id || e.activityCode === act.code);
      const uCount = evsForAct.filter(e => e.level === 'U' || e.level === 'O').length;
      const lCount = evsForAct.filter(e => e.level === 'L').length;
      const iCount = evsForAct.filter(e => e.level === 'I').length;

      row.getCell(1).value = idx + 1;
      row.getCell(2).value = act.code;
      row.getCell(3).value = act.name;
      row.getCell(4).value = act.category || 'Seguridad Industrial';
      row.getCell(5).value = act.area || 'Planta General';
      
      const riskCell = row.getCell(6);
      riskCell.value = (act.riskLevel || 'medio').toUpperCase();
      if (act.riskLevel === 'critico') {
        riskCell.font = { name: 'Arial', size: 8.5, bold: true, color: { argb: '991B1B' } };
        riskCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEE2E2' } };
      } else if (act.riskLevel === 'alto') {
        riskCell.font = { name: 'Arial', size: 8.5, bold: true, color: { argb: 'C2410C' } };
        riskCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEDD5' } };
      } else {
        riskCell.font = { name: 'Arial', size: 8.5, bold: true, color: { argb: '166534' } };
        riskCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DCFCE7' } };
      }

      row.getCell(7).value = act.standardCode || 'NOM-STPS';
      row.getCell(8).value = act.eppRequired || 'EPP Básico';
      row.getCell(9).value = `Total: ${evsForAct.length}  (U: ${uCount} | L: ${lCount} | I: ${iCount})`;

      for (let c = 1; c <= 9; c++) {
        const cell = row.getCell(c);
        if (c !== 6) {
          cell.font = { name: 'Arial', size: 8.5, color: { argb: '1E293B' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
        }
        cell.alignment = {
          vertical: 'middle',
          horizontal: [1, 2, 6, 9].includes(c) ? 'center' : 'left'
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'CBD5E1' } },
          bottom: { style: 'thin', color: { argb: 'CBD5E1' } },
          left: { style: 'thin', color: { argb: 'CBD5E1' } },
          right: { style: 'thin', color: { argb: 'CBD5E1' } }
        };
      }
      row.height = 22;
    });

    wsCat.getColumn(1).width = 6;
    wsCat.getColumn(2).width = 14;
    wsCat.getColumn(3).width = 36;
    wsCat.getColumn(4).width = 20;
    wsCat.getColumn(5).width = 20;
    wsCat.getColumn(6).width = 14;
    wsCat.getColumn(7).width = 22;
    wsCat.getColumn(8).width = 34;
    wsCat.getColumn(9).width = 26;
  }

  // Trigger Download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
}

/**
 * Exports a specific activity's folder/dossier to Excel, showing all its evaluated workers grouped by ILU level.
 */
export async function exportSingleActivityDossierToExcel(
  activity: SafetyActivity,
  evaluations: CollaboratorEvaluation[],
  options: { costCenterName?: string; generatedBy?: string } = {}
): Promise<void> {
  const {
    costCenterName = 'Sede Operativa Bajío',
    generatedBy = 'Coordinación HSEQ'
  } = options;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'SNF México - HSEQ Security & Competency Management';
  const fileName = `Expediente_Actividad_${activity.code}_${new Date().toISOString().split('T')[0]}.xlsx`;

  const ws = workbook.addWorksheet(`Actividad ${activity.code}`, {
    views: [{ showGridLines: true }]
  });

  applyCorporateHeader(
    ws,
    workbook,
    `EXPEDIENTE DE COMPETENCIA: ${activity.code} - ${activity.name.toUpperCase()}`,
    `Evaluación I.L.U. de Colaboradores Asignados a la Tarea | Nivel de Riesgo: ${(activity.riskLevel || 'medio').toUpperCase()}`,
    costCenterName,
    generatedBy,
    'K'
  );

  // Activity Metadata Panel
  let rIdx = 6;
  ws.mergeCells(`A${rIdx}:K${rIdx}`);
  const metaHead = ws.getCell(`A${rIdx}`);
  metaHead.value = 'DETALLE Y REQUERIMIENTOS TÉCNICOS DEL PROCEDIMIENTO DE SEGURIDAD';
  metaHead.font = { name: 'Arial', size: 9.5, bold: true, color: { argb: 'FFFFFF' } };
  metaHead.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1E293B' } };
  metaHead.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  ws.getRow(rIdx).height = 24;

  rIdx++;
  ws.mergeCells(`A${rIdx}:C${rIdx}`);
  ws.getCell(`A${rIdx}`).value = `🏷️ Código: ${activity.code}`;
  ws.mergeCells(`D${rIdx}:G${rIdx}`);
  ws.getCell(`D${rIdx}`).value = `📂 Categoría / Área: ${activity.category || 'General'} - ${activity.area || 'Planta'}`;
  ws.mergeCells(`H${rIdx}:K${rIdx}`);
  ws.getCell(`H${rIdx}`).value = `⚠️ Riesgo: ${(activity.riskLevel || 'medio').toUpperCase()}  |  📜 Norma: ${activity.standardCode || 'NOM-STPS'}`;
  
  for (let c = 1; c <= 11; c++) {
    const cell = ws.getRow(rIdx).getCell(c);
    cell.font = { name: 'Arial', size: 8.5, bold: true, color: { argb: '334155' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F1F5F9' } };
    cell.alignment = { vertical: 'middle', horizontal: 'left', indent: 0.5 };
  }
  ws.getRow(rIdx).height = 22;

  rIdx++;
  ws.mergeCells(`A${rIdx}:K${rIdx}`);
  const eppCell = ws.getCell(`A${rIdx}`);
  eppCell.value = `🛡️ EPP Requerido: ${activity.eppRequired || 'EPP básico de planta'}`;
  eppCell.font = { name: 'Arial', size: 8.5, italic: true, color: { argb: '475569' } };
  eppCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F8FAFC' } };
  eppCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 0.5 };
  ws.getRow(rIdx).height = 20;

  // Filter evaluations for this activity
  const actEvals = evaluations.filter(e => e.activityId === activity.id || e.activityCode === activity.code);
  const uList = actEvals.filter(e => e.level === 'U' || e.level === 'O');
  const lList = actEvals.filter(e => e.level === 'L');
  const iList = actEvals.filter(e => e.level === 'I');

  // Stats Summary
  rIdx += 2;
  ws.mergeCells(`A${rIdx}:C${rIdx}`);
  ws.getCell(`A${rIdx}`).value = `🟢 NIVEL U (AUTÓNOMOS): ${uList.length}`;
  ws.getCell(`A${rIdx}`).font = { name: 'Arial', size: 9, bold: true, color: { argb: '166534' } };
  ws.getCell(`A${rIdx}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DCFCE7' } };

  ws.mergeCells(`D${rIdx}:G${rIdx}`);
  ws.getCell(`D${rIdx}`).value = `🔵 NIVEL L (SUPERVISADOS): ${lList.length}`;
  ws.getCell(`D${rIdx}`).font = { name: 'Arial', size: 9, bold: true, color: { argb: '0369A1' } };
  ws.getCell(`D${rIdx}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E0F2FE' } };

  ws.mergeCells(`H${rIdx}:K${rIdx}`);
  ws.getCell(`H${rIdx}`).value = `🟠 NIVEL I (INDUCCIÓN): ${iList.length}`;
  ws.getCell(`H${rIdx}`).font = { name: 'Arial', size: 9, bold: true, color: { argb: 'B45309' } };
  ws.getCell(`H${rIdx}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEF3C7' } };
  ws.getRow(rIdx).height = 24;

  // Evaluated Collaborators Table
  rIdx += 2;
  const headers = [
    'No.',
    'Nómina / ID',
    'Nombre del Colaborador',
    'Puesto / Rol',
    'Área',
    'Turno',
    'Nivel I.L.U.',
    'Calificación',
    'Fecha Evaluación',
    'Auditor Evaluador',
    'Estatus Operativo'
  ];

  const thRow = ws.getRow(rIdx);
  headers.forEach((h, idx) => {
    const c = thRow.getCell(idx + 1);
    c.value = h;
    c.font = { name: 'Arial', size: 9, bold: true, color: { argb: 'FFFFFF' } };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1E293B' } };
    c.alignment = { horizontal: 'center', vertical: 'middle' };
  });
  thRow.height = 24;

  if (actEvals.length === 0) {
    rIdx++;
    ws.mergeCells(`A${rIdx}:K${rIdx}`);
    const emptyCell = ws.getCell(`A${rIdx}`);
    emptyCell.value = 'No hay colaboradores evaluados en esta actividad actualmente.';
    emptyCell.font = { name: 'Arial', size: 9.5, italic: true, color: { argb: '64748B' } };
    emptyCell.alignment = { horizontal: 'center', vertical: 'middle' };
    ws.getRow(rIdx).height = 30;
  } else {
    actEvals.forEach((ev, idx) => {
      rIdx++;
      const row = ws.getRow(rIdx);
      const isEven = idx % 2 === 0;
      const bg = isEven ? 'FFFFFF' : 'F8FAFC';

      row.getCell(1).value = idx + 1;
      row.getCell(2).value = ev.employeeId || 'S/N';
      row.getCell(3).value = ev.employeeName;
      row.getCell(4).value = ev.position || 'Operador';
      row.getCell(5).value = ev.area || 'General';
      row.getCell(6).value = ev.shift || 'Turno 1';
      
      const lvlCell = row.getCell(7);
      lvlCell.value = ev.level;
      if (ev.level === 'U' || ev.level === 'O') {
        lvlCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DCFCE7' } };
        lvlCell.font = { name: 'Arial', size: 10, bold: true, color: { argb: '166534' } };
      } else if (ev.level === 'L') {
        lvlCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E0F2FE' } };
        lvlCell.font = { name: 'Arial', size: 10, bold: true, color: { argb: '0369A1' } };
      } else {
        lvlCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEF3C7' } };
        lvlCell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'B45309' } };
      }

      row.getCell(8).value = ev.score ? `${ev.score}%` : '100%';
      row.getCell(9).value = ev.evaluationDate || '-';
      row.getCell(10).value = ev.evaluator || 'Líder HSEQ';
      row.getCell(11).value = ev.status === 'apto' ? 'APTO / CERTIFICADO' : (ev.status === 'en_desarrollo' ? 'EN DESARROLLO' : 'REQUIERE REFUERZO');

      for (let c = 1; c <= 11; c++) {
        const cell = row.getCell(c);
        if (c !== 7) {
          cell.font = { name: 'Arial', size: 8.5, color: { argb: '1E293B' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
        }
        cell.alignment = {
          vertical: 'middle',
          horizontal: [1, 2, 6, 7, 8, 9, 11].includes(c) ? 'center' : 'left'
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'CBD5E1' } },
          bottom: { style: 'thin', color: { argb: 'CBD5E1' } },
          left: { style: 'thin', color: { argb: 'CBD5E1' } },
          right: { style: 'thin', color: { argb: 'CBD5E1' } }
        };
      }
      row.height = 20;
    });
  }

  ws.getColumn(1).width = 6;
  ws.getColumn(2).width = 14;
  ws.getColumn(3).width = 28;
  ws.getColumn(4).width = 24;
  ws.getColumn(5).width = 18;
  ws.getColumn(6).width = 16;
  ws.getColumn(7).width = 12;
  ws.getColumn(8).width = 14;
  ws.getColumn(9).width = 14;
  ws.getColumn(10).width = 24;
  ws.getColumn(11).width = 22;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
}

/**
 * Generates an official Excel template for bulk importing Safety Activities (.xlsx).
 */
export async function generateActivitiesTemplateExcel(): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'SNF México - HSEQ Security';
  const fileName = 'Plantilla_Carga_Masiva_Actividades_Seguridad.xlsx';

  // Sheet 1: Plantilla
  const ws = workbook.addWorksheet('Plantilla Actividades', {
    views: [{ showGridLines: true }]
  });

  // Header Banner
  ws.mergeCells('A1:G1');
  const h1 = ws.getCell('A1');
  h1.value = 'PLANTILLA OFICIAL PARA CARGA MASIVA DE ACTIVIDADES Y COMPETENCIAS DE SEGURIDAD';
  h1.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FFFFFF' } };
  h1.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'BC1100' } };
  h1.alignment = { horizontal: 'center', vertical: 'middle' };
  ws.getRow(1).height = 28;

  ws.mergeCells('A2:G2');
  const h2 = ws.getCell('A2');
  h2.value = 'Complete los campos obligatorios (*) y guarde este archivo para importarlo en el panel de Matriz de Competencias.';
  h2.font = { name: 'Arial', size: 8.5, italic: true, color: { argb: '475569' } };
  h2.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F1F5F9' } };
  h2.alignment = { horizontal: 'center', vertical: 'middle' };
  ws.getRow(2).height = 20;

  // Table Headers
  const columns = [
    { title: 'Código de Actividad *', desc: 'Ej: LOTO-01, ALT-02, QUIM-03' },
    { title: 'Nombre de la Actividad *', desc: 'Ej: Bloqueo y Etiquetado de Energía' },
    { title: 'Categoría / Área *', desc: 'Ej: Trabajos Peligrosos, Químicos, Mantenimiento' },
    { title: 'Nivel de Riesgo *', desc: 'Opciones: Bajo / Medio / Alto / Crítico' },
    { title: 'Norma o Estándar', desc: 'Ej: NOM-004-STPS-1999, NOM-009-STPS-2011' },
    { title: 'EPP Requerido', desc: 'Ej: Casco, Lentes, Guantes, Arnés' },
    { title: 'Descripción Técnica', desc: 'Breve explicación del procedimiento' }
  ];

  const thRow = ws.getRow(4);
  columns.forEach((c, idx) => {
    const cell = thRow.getCell(idx + 1);
    cell.value = c.title;
    cell.font = { name: 'Arial', size: 9, bold: true, color: { argb: 'FFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1E293B' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
  });
  thRow.height = 24;

  // Sample Rows
  const sampleRows = [
    ['LOTO-01', 'Bloqueo y Etiquetado de Energía Peligrosa (LOTO)', 'Trabajos Peligrosos', 'Crítico', 'NOM-004-STPS', 'Lentes, Guantes dieléctricos, Aldaba LOTO', 'Aislamiento de fuentes eléctricas y neumáticas'],
    ['ALT-02', 'Trabajos Seguros en Alturas (>1.80m)', 'Trabajos Peligrosos', 'Alto', 'NOM-009-STPS', 'Arnés de 4 puntos, Eslinga, Casco con barboquejo', 'Uso correcto de sistema anticaídas'],
    ['QUIM-03', 'Manejo Seguro de Sustancias Químicas', 'Sustancias Químicas', 'Alto', 'NOM-018-STPS', 'Mandil PVC, Guantes de nitrilo, Googles químicos', 'Interpretación SGA y trasvase seguro'],
    ['MONT-04', 'Operación Segura de Montacargas y Estiba', 'Maquinaria y Transporte', 'Medio', 'NOM-006-STPS', 'Chaleco reflejante, Casco, Calzado de seguridad', 'Checklist diario y respeto a pasos peatonales'],
    ['DER-05', 'Respuesta Inmediata a Derrames Químicos', 'Emergencias', 'Alto', 'NOM-005-STPS', 'Traje Tyvek, Botas de hule, Guantes de butilo', 'Contención con cordones absorbentes y kit']
  ];

  sampleRows.forEach((rowValues, rIdx) => {
    const row = ws.getRow(5 + rIdx);
    rowValues.forEach((val, cIdx) => {
      const cell = row.getCell(cIdx + 1);
      cell.value = val;
      cell.font = { name: 'Arial', size: 8.5, color: { argb: '1E293B' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rIdx % 2 === 0 ? 'FFFFFF' : 'F8FAFC' } };
      cell.alignment = { vertical: 'middle', horizontal: cIdx === 0 || cIdx === 3 ? 'center' : 'left' };
      cell.border = {
        top: { style: 'thin', color: { argb: 'CBD5E1' } },
        bottom: { style: 'thin', color: { argb: 'CBD5E1' } },
        left: { style: 'thin', color: { argb: 'CBD5E1' } },
        right: { style: 'thin', color: { argb: 'CBD5E1' } }
      };
    });
    row.height = 20;
  });

  ws.getColumn(1).width = 16;
  ws.getColumn(2).width = 36;
  ws.getColumn(3).width = 24;
  ws.getColumn(4).width = 16;
  ws.getColumn(5).width = 22;
  ws.getColumn(6).width = 34;
  ws.getColumn(7).width = 40;

  // Sheet 2: Instrucciones
  const wsInst = workbook.addWorksheet('Instrucciones de Llenado', {
    views: [{ showGridLines: true }]
  });

  wsInst.mergeCells('A1:E1');
  const instTitle = wsInst.getCell('A1');
  instTitle.value = 'GUÍA DE LLENADO Y VALIDACIÓN - CARGA MASIVA DE ACTIVIDADES';
  instTitle.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FFFFFF' } };
  instTitle.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1E293B' } };
  instTitle.alignment = { horizontal: 'center', vertical: 'middle' };
  wsInst.getRow(1).height = 28;

  const guideLines = [
    { col: 'Código de Actividad', rule: 'Obligatorio. Identificador alfanumérico único (ej. LOTO-01, ALT-02).' },
    { col: 'Nombre de la Actividad', rule: 'Obligatorio. Título descriptivo de la tarea de seguridad.' },
    { col: 'Categoría / Área', rule: 'Obligatorio. Clasificación operativa (ej. Trabajos Peligrosos, Emergencias).' },
    { col: 'Nivel de Riesgo', rule: 'Obligatorio. Valores permitidos: Bajo, Medio, Alto, Crítico.' },
    { col: 'Norma o Estándar', rule: 'Opcional. Estándar STPS o ISO aplicable.' },
    { col: 'EPP Requerido', rule: 'Opcional. Lista de equipos de protección obligatorios.' },
    { col: 'Descripción Técnica', rule: 'Opcional. Detalles adicionales para la evaluación.' }
  ];

  const guideThRow = wsInst.getRow(3);
  guideThRow.getCell(1).value = 'Campo / Columna';
  guideThRow.getCell(2).value = 'Regla y Validación de Datos';
  guideThRow.getCell(1).font = { name: 'Arial', size: 9, bold: true, color: { argb: 'FFFFFF' } };
  guideThRow.getCell(2).font = { name: 'Arial', size: 9, bold: true, color: { argb: 'FFFFFF' } };
  guideThRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '334155' } };
  guideThRow.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '334155' } };
  wsInst.mergeCells('B3:E3');
  guideThRow.height = 22;

  guideLines.forEach((g, idx) => {
    const row = wsInst.getRow(4 + idx);
    row.getCell(1).value = g.col;
    row.getCell(2).value = g.rule;
    wsInst.mergeCells(`B${4 + idx}:E${4 + idx}`);
    row.getCell(1).font = { name: 'Arial', size: 8.5, bold: true, color: { argb: '1E293B' } };
    row.getCell(2).font = { name: 'Arial', size: 8.5, color: { argb: '334155' } };
    row.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: idx % 2 === 0 ? 'F8FAFC' : 'FFFFFF' } };
    row.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: idx % 2 === 0 ? 'F8FAFC' : 'FFFFFF' } };
    row.height = 20;
  });

  wsInst.getColumn(1).width = 24;
  wsInst.getColumn(2).width = 45;
  wsInst.getColumn(3).width = 15;
  wsInst.getColumn(4).width = 15;
  wsInst.getColumn(5).width = 15;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
}

/**
 * Generates an official Excel template for bulk importing Collaborator Evaluations (.xlsx).
 */
export async function generateEvaluationsTemplateExcel(activities: SafetyActivity[] = []): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'SNF México - HSEQ Security';
  const fileName = 'Plantilla_Carga_Masiva_Evaluaciones_ILU.xlsx';

  const ws = workbook.addWorksheet('Plantilla Evaluaciones', {
    views: [{ showGridLines: true }]
  });

  ws.mergeCells('A1:K1');
  const h1 = ws.getCell('A1');
  h1.value = 'PLANTILLA OFICIAL PARA EVALUACIÓN MASIVA DE COLABORADORES (MÉTODO I.L.U.)';
  h1.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FFFFFF' } };
  h1.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'BC1100' } };
  h1.alignment = { horizontal: 'center', vertical: 'middle' };
  ws.getRow(1).height = 28;

  ws.mergeCells('A2:K2');
  const h2 = ws.getCell('A2');
  h2.value = 'Asigne el Nivel I.L.U. [ I = Inducción | L = Supervisado | U = Autónomo | O = Instructor ] a cada colaborador por código de actividad.';
  h2.font = { name: 'Arial', size: 8.5, italic: true, color: { argb: '475569' } };
  h2.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F1F5F9' } };
  h2.alignment = { horizontal: 'center', vertical: 'middle' };
  ws.getRow(2).height = 20;

  const columns = [
    'Código Actividad *',
    'No. Nómina / ID *',
    'Nombre del Colaborador *',
    'Puesto / Cargo *',
    'Área Operativa *',
    'Turno *',
    'Nivel I.L.U. *',
    'Calificación (0-100)',
    'Fecha Evaluación (YYYY-MM-DD)',
    'Auditor / Evaluador',
    'Observaciones / Evidencia'
  ];

  const thRow = ws.getRow(4);
  columns.forEach((c, idx) => {
    const cell = thRow.getCell(idx + 1);
    cell.value = c;
    cell.font = { name: 'Arial', size: 9, bold: true, color: { argb: 'FFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1E293B' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
  });
  thRow.height = 24;

  const todayStr = new Date().toISOString().split('T')[0];
  const sampleRows = [
    ['LOTO-01', 'EMP-1042', 'Carlos Morales Hernández', 'Técnico Electromecánico', 'Mantenimiento', 'Turno 1 - Matutino', 'U', 100, todayStr, 'Ing. Roberto Silva', 'Demostró colocación correcta de candados y prueba energía cero'],
    ['LOTO-01', 'EMP-1088', 'Alejandro Mendoza Soto', 'Operador de Reactores', 'Producción', 'Turno 2 - Vespertino', 'L', 85, todayStr, 'Ing. Roberto Silva', 'Aplica candado pero requiere supervisión en purga de líneas'],
    ['LOTO-01', 'EMP-1120', 'Fernando Ríos Domínguez', 'Ayudante General', 'Mantenimiento', 'Turno 3 - Nocturno', 'I', 65, todayStr, 'Ing. Sandra Valenzuela', 'En inducción teórica. No autorizado para colocar candados solo'],
    ['ALT-02', 'EMP-1042', 'Carlos Morales Hernández', 'Técnico Electromecánico', 'Mantenimiento', 'Turno 1 - Matutino', 'U', 98, todayStr, 'Ing. Sandra Valenzuela', 'Inspección de arnés sin observaciones. Apto para alturas'],
    ['MONT-04', 'EMP-1055', 'Martín Delgado Corona', 'Montacarguista', 'Almacén', 'Turno 1 - Matutino', 'U', 100, todayStr, 'Ing. Roberto Silva', 'Licencia y DC-3 vigentes. Maniobras seguras']
  ];

  sampleRows.forEach((rowValues, rIdx) => {
    const row = ws.getRow(5 + rIdx);
    rowValues.forEach((val, cIdx) => {
      const cell = row.getCell(cIdx + 1);
      cell.value = val;
      cell.font = { name: 'Arial', size: 8.5, color: { argb: '1E293B' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rIdx % 2 === 0 ? 'FFFFFF' : 'F8FAFC' } };
      cell.alignment = { vertical: 'middle', horizontal: [0, 1, 5, 6, 7, 8].includes(cIdx) ? 'center' : 'left' };
      cell.border = {
        top: { style: 'thin', color: { argb: 'CBD5E1' } },
        bottom: { style: 'thin', color: { argb: 'CBD5E1' } },
        left: { style: 'thin', color: { argb: 'CBD5E1' } },
        right: { style: 'thin', color: { argb: 'CBD5E1' } }
      };
    });
    row.height = 20;
  });

  ws.getColumn(1).width = 16;
  ws.getColumn(2).width = 14;
  ws.getColumn(3).width = 28;
  ws.getColumn(4).width = 24;
  ws.getColumn(5).width = 18;
  ws.getColumn(6).width = 20;
  ws.getColumn(7).width = 12;
  ws.getColumn(8).width = 14;
  ws.getColumn(9).width = 16;
  ws.getColumn(10).width = 22;
  ws.getColumn(11).width = 36;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
}
